import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import {
  SportsEsportsOutlined as TileIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";

/**
 * MineGridPreview Component
 * Displays a lightweight visual minefield grid preview depicting the ticket slots
 */
const MineGridPreview = ({ capacity = 20, maxPreviewTiles = 12 }) => {
  const totalSlots = Math.max(4, Number(capacity) || 20);
  const displayTiles = Math.min(totalSlots, maxPreviewTiles);
  const remainingCount = totalSlots - displayTiles;

  const tiles = useMemo(() => {
    const list = [];
    for (let i = 1; i <= displayTiles; i++) {
      list.push(i);
    }
    return list;
  }, [displayTiles]);

  return (
    <Box
      sx={{
        bgcolor: colors.slate[50],
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: borderRadiusPx.lg,
        p: 1.5,
        my: 1.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="caption"
          sx={{
            color: colors.purple[700],
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: "0.7rem",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <TileIcon sx={{ fontSize: 13, color: colors.purple[600] }} />
          Minefield Matrix
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: colors.slate[500],
            fontWeight: 600,
            fontSize: "0.72rem",
          }}
        >
          {totalSlots} Total Slots
        </Typography>
      </Stack>

      {/* Mini Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: 0.75,
        }}
      >
        {tiles.map((num) => (
          <Box
            key={num}
            sx={{
              height: 26,
              borderRadius: borderRadiusPx.xs,
              bgcolor: colors.dashboard.cardBg,
              border: `1px solid ${colors.purple[200]}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.68rem",
              fontWeight: 700,
              color: colors.purple[800],
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: colors.purple[600],
                color: colors.neutral.white,
                borderColor: colors.purple[600],
                transform: "scale(1.08)",
              },
            }}
          >
            {num}
          </Box>
        ))}
      </Box>

      {remainingCount > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: colors.slate[400],
            fontWeight: 500,
            fontSize: "0.68rem",
            mt: 0.75,
          }}
        >
          +{remainingCount} more interactive tiles on game board
        </Typography>
      )}
    </Box>
  );
};

MineGridPreview.propTypes = {
  capacity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxPreviewTiles: PropTypes.number,
};

export default MineGridPreview;
